'use client';

import { useEffect, useRef } from 'react';
import { ZERO } from '@velocity-exchange/sdk';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import { notify } from 'src/utils/notifications';
import useNotificationStore from 'src/stores/useNotificationStore';
import { useAccountsBeingLiquidatedWithoutBankruptcies } from './useAccountsBeingLiquidated';
import usePositionsBeingLiquidated from './usePositionsBeingLiquidated';
import useModal from 'src/components/Modals/hooks/useModal';
import Link from 'src/components/primitives/Link/Link';
import Button from 'src/components/primitives/Button/Button';
import { UIMarket } from '@velocity-exchange/common';
import { VELOCITY_DOCS_URL } from 'src/constants/constants';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useCurrentAuthority from '../useCurrentAuthority';

const useLiquidationAlerts = () => {
	const { show } = useModal();
	const accountsBeingLiquidated =
		useAccountsBeingLiquidatedWithoutBankruptcies();
	const positionsBeingLiquidated = usePositionsBeingLiquidated();
	const currentUserKey = useVelocityAccountStore((s) => s.currentUserKey);
	const accountsMap = useVelocityAccountStore((s) => s.accounts);
	const removeNotificationById = useNotificationStore(
		(s) => s.removeNotificationById
	);
	const isConnected = useWalletIsConnected();
	const authorityStr = useCurrentAuthority()?.toString() ?? '';

	// Toast ids already shown this session (survives dismiss). Cleared on
	// disconnect / authority change so a new signer starts fresh.
	const shownThisSessionRef = useRef<Set<string>>(new Set());

	// `alertOpen_*` ids with a live toast on screen. Nothing else clears a
	// `showUntilCancelled` toast, so an id dropped from here leaks for the
	// life of the page.
	const shownAlertOpenIdsRef = useRef<Set<string>>(new Set());

	const clearAllTrackedAlerts = () => {
		shownAlertOpenIdsRef.current.forEach((id) => removeNotificationById(id));
		shownAlertOpenIdsRef.current.clear();
		shownThisSessionRef.current.clear();
	};

	// Wallet disconnect: nothing left to alert on, and a future reconnect
	// (possibly a different wallet) should start from a clean slate.
	useEffect(() => {
		if (!isConnected) {
			clearAllTrackedAlerts();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isConnected]);

	// Authority change: a different signer means these ids no longer refer
	// to anything real, so drop them rather than let a stale id leak across.
	const prevAuthorityRef = useRef(authorityStr);
	useEffect(() => {
		if (prevAuthorityRef.current !== authorityStr) {
			clearAllTrackedAlerts();
			prevAuthorityRef.current = authorityStr;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authorityStr]);

	// Unmount: don't leave toasts orphaned on screen after this hook stops
	// running its diff.
	useEffect(() => {
		return () => {
			clearAllTrackedAlerts();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		accountsBeingLiquidated.forEach((alertToAdd) => {
			const alertId = `alertOpen_${alertToAdd.userKey}`;

			if (shownThisSessionRef.current.has(alertId)) return;

			shownThisSessionRef.current.add(alertId);
			shownAlertOpenIdsRef.current.add(alertId);

			const isCurrentAccount = alertToAdd.userKey === currentUserKey;
			const accountName = accountsMap[alertToAdd.userKey]?.name ?? 'Account';

			const description = isCurrentAccount
				? 'Your account is being liquidated'
				: `Your account (${accountName}) is being liquidated`;

			notify({
				type: 'awaiting',
				id: alertId,
				message: 'LIQUIDATION IN PROGRESS',
				description,
				showUntilCancelled: true,
				updatePrevious: true,
				action: {
					type: 'custom',
					content: (
						<div className="flex items-center gap-x-sm">
							<Button
								size="sm"
								variant="stroke"
								onClick={() => {
									show({
										type: 'deposit',
										props: {
											targetAccountKey: alertToAdd.userKey,
											defaultLargestBalance: true,
										},
									});
								}}
							>
								Deposit
							</Button>
							<Link
								href={`${VELOCITY_DOCS_URL}/liquidations/liquidations`}
								openInNewTab
							>
								Learn More
							</Link>
						</div>
					),
				},
			});
		});

		// Isolated position liquidation alerts (per position)
		positionsBeingLiquidated.forEach((item) => {
			const marketIndex = item.position.marketIndex;
			const isFullyLiquidated = item.position.baseAssetAmount.eq(ZERO);

			const openAlertId = `alertOpen_${item.userKey}_${marketIndex}`;
			const alertId = isFullyLiquidated
				? `alertComplete_${item.userKey}_${marketIndex}`
				: openAlertId;

			// Open and complete are separate session keys so an in-progress toast
			// can still be superseded by a one-time completion toast.
			if (shownThisSessionRef.current.has(alertId)) return;

			shownThisSessionRef.current.add(alertId);

			const uiMarket = UIMarket.createPerpMarket(marketIndex);
			const marketLabel = uiMarket.uiSymbols.marketDisplaySymbol ?? '';

			const isCurrentAccount = item.userKey === currentUserKey;
			const accountName = accountsMap[item.userKey]?.name ?? 'Account';

			// Qualify with the account name only when it isn't the one on screen,
			// matching the account-level alert above.
			const description = `${isCurrentAccount ? '' : `Your account (${accountName})'s `}${marketLabel} (Isolated) is ${isFullyLiquidated ? '' : 'being '}liquidated`;

			if (isFullyLiquidated) {
				// Supersede the in-progress toast with the completion toast —
				// otherwise the "in progress" toast (showUntilCancelled) lingers
				// alongside the new "complete" toast forever.
				if (shownAlertOpenIdsRef.current.has(openAlertId)) {
					removeNotificationById(openAlertId);
					shownAlertOpenIdsRef.current.delete(openAlertId);
				}
			} else {
				shownAlertOpenIdsRef.current.add(openAlertId);
			}

			notify({
				type: isFullyLiquidated ? 'alert' : 'awaiting',
				id: alertId,
				message: isFullyLiquidated
					? 'LIQUIDATION COMPLETE'
					: 'LIQUIDATION IN PROGRESS',
				description,
				showUntilCancelled: !isFullyLiquidated,
				updatePrevious: true,
			});
		});

		// Every id the current liquidation state would produce. Anything tracked
		// but absent from this set belongs to an episode that has ended.
		const liveIds = new Set([
			...accountsBeingLiquidated.map(
				(account) => `alertOpen_${account.userKey}`
			),
			...positionsBeingLiquidated.map((item) =>
				item.position.baseAssetAmount.eq(ZERO)
					? `alertComplete_${item.userKey}_${item.position.marketIndex}`
					: `alertOpen_${item.userKey}_${item.position.marketIndex}`
			),
		]);

		shownAlertOpenIdsRef.current.forEach((id) => {
			if (!liveIds.has(id)) {
				removeNotificationById(id);
				shownAlertOpenIdsRef.current.delete(id);
			}
		});

		// Release the once-per-session suppression when the episode it refers to
		// is over, so a *second* liquidation of the same account or market still
		// alerts. Ids are only held while their episode is live — the set exists
		// to survive a user dismissing a toast, not to mute the rest of the
		// session.
		shownThisSessionRef.current.forEach((id) => {
			if (!liveIds.has(id)) {
				shownThisSessionRef.current.delete(id);
			}
		});
	}, [
		accountsBeingLiquidated,
		positionsBeingLiquidated,
		show,
		currentUserKey,
		accountsMap,
		removeNotificationById,
	]);
};

export default useLiquidationAlerts;
